import { TokenInfo } from '@labb/constellation-core-types';
import { PegaEmbed } from '@labb/react-adapter';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import AccountAlreadyExistsScreen from '../components/AccountAlreadyExistsScreen.tsx';
import { BaseScreen } from '../components/BaseScreen.tsx';
import CfdAddOnModal from '../components/CfdAddOnModal.tsx';
import ErrorMessage from '../components/ErrorMessage.tsx';
import InProgressApplicationsModal from '../components/InProgressApplicationsModal.tsx';
import ThankYouScreenAddOn from '../components/ThankYouScreenAddOn.tsx';
import ThankYouScreenCorporate from '../components/ThankYouScreenCorporate.tsx';
import ThankYouScreenMetaTrader from '../components/ThankYouScreenMetaTrader.tsx';
import ThankYouScreenMultiAsset from '../components/ThankYouScreenMultiAsset.tsx';
import ThankYouScreenSpectre from '../components/ThankYouScreenSpectre.tsx';
import ThankYouScreenSumsub from '../components/ThankYouScreenSumsub.tsx';
import { useMainContext } from '../context/MainContext.tsx';
import { useMergedConfig } from '../context/MergedConfigProvider.tsx';
import { usePegaEventsContext } from '../context/PegaEventContext.tsx';
import { syncTradingTypeFromAppContextProduct, useMParticle } from '../hooks/useMparticle.ts';
import { useTranslations } from '../hooks/useTranslations.ts';
import addOnConfig from '../journey-configs/addOn.json';
import { API_ERROR_CODES } from '../onboarding-api';
import { ensureFlagsCssLoaded } from '../utils/flagsCss.ts';
import { getEffectiveLocale, mapRegionForLocale } from '../utils/getEffectiveLocale.ts';
import { getTranslatedApiError } from '../utils/getTranslatedApiError.ts';
import { getPegaAppAlias, loadPegaCredentials } from '../utils/loadPegaCredentials.ts';
import logger from '../utils/logger.ts';
import { resolveQueryRegion } from '../utils/resolveQueryRegion.ts';
import { useIsMobile } from '../utils/useIsMobile.ts';
import { registerMapping } from './pega/PegaMapping.ts';
import {
  apiGetPegaState,
  isEmptyPegaStateResponse,
  PegaApplication,
  PegaApplicationStatus,
  PegaState,
  PegaStateResponse,
  resolvePegaStateResponse,
} from './pega/apiGetPegaState.ts';
import { apiGetProducts } from './pega/apiGetProducts.ts';
import { getPegaToken } from './pega/getPegaToken.ts';

const caseTypeId = 'CMC-Onboarding-Work-Application';
const pegaBaseUrl = import.meta.env.VITE_PEGA_BASE_URL;

const normalizeProduct = (value?: string | null) => (value ?? '').trim().toUpperCase();

const NON_EQUITY_JOURNEY_PRODUCTS = new Set(['CFD', 'SB', 'SPECTRE', 'SPECTRERETAIL']);

type Props = {
  oneTimeToken?: string;
  pegaToken?: TokenInfo;
  iaid: null | string;
  embedOneTimeToken: string | null;
  csrfToken: string;
  userEmail?: string;
  userPassword?: string;
  assignmentKey?: string;
  pegaStartingFields?: {
    IAID?: string | null;
    ApplicationVersion?: string | null;
    MultiAsset?: boolean | null;
    Product?: string | null;
    AccountID?: string | null;
    TenantID?: string | null;
    Context?: string | null;
    OnboardingRegion?: string | null;
    Domain?: string | null;
    Platform?: string | null;
    CustomerType?: string | null;
    IsFromPlatform?: boolean | null;
    Channel?: string | null;
    ConnectApplicationID?: string | null;
    CaseID?: string | null;
  };
};

export const PegaJourney: FC<Props> = ({
  oneTimeToken,
  pegaToken,
  pegaStartingFields,
  embedOneTimeToken,
  csrfToken,
  userEmail,
  userPassword,
  assignmentKey,
}) => {
  const {
    initPegaSession,
    subscribeToPegaEvents,
    isEndOfAssignment,
    resetEndOfAssignment,
    resetAppContext,
    onPegaTokenExpiry,
    appContext,
  } = usePegaEventsContext();
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location]);
  const mergedConfig = useMergedConfig();
  const { setPegaToken } = useMainContext();
  const { lang, region, embedded } = mergedConfig;
  const { logLiveAppSubmit, setTradingType } = useMParticle();
  const { translate } = useTranslations();
  const isMobile = useIsMobile();
  const channel = isMobile ? 'Mobile' : 'Web';

  const urlRegion = resolveQueryRegion(query);
  const urlLocale = query.get('locale');
  const localeRegion = urlRegion || region || undefined;

  const effectiveRegion = mapRegionForLocale(localeRegion);
  const effectiveLocale = getEffectiveLocale({
    lang,
    region: localeRegion,
    urlLocale,
    urlRegion,
  });

  const currentSearchString = useRef(query.toString());
  const journeyStartedRef = useRef(false);

  const [pegaState, setPegaState] = useState<PegaState | undefined>();
  const pegaStateRef = useRef(pegaState);
  const [loadingStatus, setLoadingStatus] = useState<boolean | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [applicationSubmitted, setApplicationSubmitted] = useState(false);
  const [addOnModalDismissed, setAddOnModalDismissed] = useState(false);
  const [isAddOnJourney, setIsAddOnJourney] = useState(false);
  const [pendingApplications, setPendingApplications] = useState<PegaApplication[] | undefined>();
  const [canAddAccount, setCanAddAccount] = useState(true);

  useEffect(() => {
    pegaStateRef.current = pegaState;
  }, [pegaState]);

  useEffect(() => {
    if (assignmentKey) {
      journeyStartedRef.current = true;
      setPendingApplications(undefined);
      setPegaState({
        applicationAssignmentKey: assignmentKey,
        applicationStatus: PegaApplicationStatus['Open-InProgress'],
      });
      initPegaSession();
    }
  }, [assignmentKey, initPegaSession]);

  const handlePegaStateResponse = useCallback(
    (data: PegaStateResponse) => {
      const resolved = resolvePegaStateResponse(data);

      if (resolved.kind === 'list' && resolved.applications.length > 0) {
        if (!journeyStartedRef.current) {
          setPendingApplications(resolved.applications);
          return;
        }
        if (pegaStateRef.current) {
          return;
        }
      }

      const state =
        resolved.kind === 'single'
          ? resolved.state
          : {
              applicationAssignmentKey: '',
              applicationStatus: PegaApplicationStatus.Empty,
            };

      journeyStartedRef.current = true;
      setPendingApplications(undefined);
      setPegaState(state);
      initPegaSession();
    },
    [initPegaSession]
  );

  const handleSelectApplication = useCallback(
    (application: PegaApplication) => {
      journeyStartedRef.current = true;
      setPendingApplications(undefined);
      setPegaState({
        applicationAssignmentKey: application.applicationAssignmentKey,
        applicationStatus: application.applicationStatus,
      });
      initPegaSession();
    },
    [initPegaSession]
  );

  const handleStartNewApplication = useCallback(() => {
    if (!canAddAccount) {
      return;
    }
    journeyStartedRef.current = true;
    setPendingApplications(undefined);
    setPegaState({
      applicationAssignmentKey: '',
      applicationStatus: PegaApplicationStatus.Empty,
    });
    initPegaSession();
  }, [canAddAccount, initPegaSession]);

  const loadPegaStateWithProductsGate = useCallback(
    async (token: TokenInfo, startingFields: typeof pegaStartingFields & { Channel?: string | null }) => {
      const { data, errorCode } = await apiGetPegaState(pegaBaseUrl, token, startingFields);

      if (!data) {
        if (errorCode === API_ERROR_CODES.PEGA_TOKEN_EXPIRED) {
          onPegaTokenExpiry();
        } else if (errorCode) {
          setError(getTranslatedApiError(errorCode, translate));
        }
        return;
      }

      const isEmpty = isEmptyPegaStateResponse(data);
      const resolved = resolvePegaStateResponse(data);
      const hasPendingList = resolved.kind === 'list' && resolved.applications.length > 0;

      if (isEmpty || hasPendingList) {
        const productsResult = await apiGetProducts(pegaBaseUrl, token, startingFields);

        if (productsResult.errorCode === API_ERROR_CODES.PEGA_TOKEN_EXPIRED) {
          onPegaTokenExpiry();
          return;
        }

        const productsCanAddAccount = productsResult.data?.canAddAccount !== false;
        setCanAddAccount(productsCanAddAccount);

        if (isEmpty && !productsCanAddAccount) {
          journeyStartedRef.current = true;
          setPendingApplications(undefined);
          setPegaState({
            applicationAssignmentKey: '',
            applicationStatus: PegaApplicationStatus['Resolved-Completed'],
          });
          return;
        }
      }

      handlePegaStateResponse(data);
    },
    [handlePegaStateResponse, onPegaTokenExpiry, translate]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: Dependencies intentionally omitted
  useEffect(() => {
    registerMapping();

    if (pegaToken) {
      return;
    }

    if (oneTimeToken || embedOneTimeToken) {
      const { clientId, clientSecret, identityToken } = loadPegaCredentials(oneTimeToken, embedOneTimeToken);

      getPegaToken(pegaBaseUrl, clientId, clientSecret, identityToken, effectiveRegion, effectiveLocale)
        .then((token) => {
          setPegaToken(token);
        })
        .catch((tokenError: unknown) => {
          logger.error(
            'Failed to obtain Pega access token: ' +
              (tokenError instanceof Error ? tokenError.message : String(tokenError))
          );
          setError(getTranslatedApiError(API_ERROR_CODES.GENERIC, translate));
        });
    } else {
      logger.error('no oneTimeToken or embedOneTimeToken provided to PegaJourney');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (pegaToken) {
      void ensureFlagsCssLoaded().catch(() => {
        // Prevent unhandled promise rejections; error already logged in ensureFlagsCssLoaded.
      });

      if (!assignmentKey) {
        void loadPegaStateWithProductsGate(pegaToken, {
          ...pegaStartingFields,
          Channel: channel,
        });
      }
    }
  }, [loadPegaStateWithProductsGate, pegaToken, pegaStartingFields, channel, assignmentKey]);

  useEffect(() => {
    syncTradingTypeFromAppContextProduct(appContext.Product);
  }, [appContext.Product]);

  useEffect(() => {
    const isCompleted =
      isEndOfAssignment || pegaState?.applicationStatus === PegaApplicationStatus['Resolved-Completed'];

    if (isCompleted && !applicationSubmitted) {
      setApplicationSubmitted(true);
      logLiveAppSubmit();
    }
  }, [isEndOfAssignment, pegaState, applicationSubmitted, logLiveAppSubmit]);

  useEffect(() => {
    const oldSearch = currentSearchString.current;
    const newSearch = window.location.search;
    const queryParamsHasChanged = newSearch !== oldSearch;

    if (queryParamsHasChanged && newSearch === '' && !embedded) {
      const oldParams = new URLSearchParams(oldSearch);
      const newParams = new URLSearchParams(newSearch);
      oldParams.forEach((value, key) => {
        newParams.set(key, value);
      });
      const newUrl = `${window.location.pathname}?${newParams.toString()}`;
      window.history.replaceState({}, '', newUrl);
      currentSearchString.current = newParams.toString();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.search]);

  const handleAddOnConfirm = useCallback(() => {
    setIsAddOnJourney(true);
    setTradingType('CFD');
    setPegaState(undefined);
    setPendingApplications(undefined);
    setCanAddAccount(true);
    setLoadingStatus(undefined);
    setApplicationSubmitted(false);
    resetEndOfAssignment();
    resetAppContext();

    if (pegaToken) {
      void loadPegaStateWithProductsGate(pegaToken, {
        ...addOnConfig.pegaStartingFields,
        Channel: channel,
      });
    }
  }, [pegaToken, loadPegaStateWithProductsGate, channel, setTradingType, resetEndOfAssignment, resetAppContext]);

  const handleAddOnDismiss = useCallback(() => {
    setAddOnModalDismissed(true);
  }, []);

  const isCancelled = appContext.IsCancelled === true || appContext.IsCancelled === 'true';
  const isJourneyComplete =
    isEndOfAssignment || pegaState?.applicationStatus === PegaApplicationStatus['Resolved-Completed'];
  const resolvedBeforeEmbedLoaded =
    pegaState?.applicationStatus === PegaApplicationStatus['Resolved-Completed'] && loadingStatus === undefined;

  // The add-on journey keeps its own thank-you screens for both cancelled and completed cases
  const showAccountAlreadyExists = !isAddOnJourney && (resolvedBeforeEmbedLoaded || (isJourneyComplete && isCancelled));

  if (showAccountAlreadyExists) {
    return (
      <AccountAlreadyExistsScreen
        embedded={embedded}
        csrfToken={csrfToken}
        userEmail={userEmail}
        userPassword={userPassword}
      />
    );
  }

  if (isEndOfAssignment || pegaState?.applicationStatus === PegaApplicationStatus['Resolved-Completed']) {
    // Resolve params — prefer appContext from Pega, fall back to pegaStartingFields
    const platform = (appContext.Platform || pegaStartingFields?.Platform || '').toUpperCase();
    const customerType = (appContext.CustomerType || pegaStartingFields?.CustomerType || '').toUpperCase();
    const runtimeProduct = normalizeProduct(appContext.Product);
    const product = normalizeProduct(appContext.Product || pegaStartingFields?.Product);
    const journeyProduct = normalizeProduct(pegaStartingFields?.Product);
    const context = (appContext.Context || pegaStartingFields?.Context || 'Live') as 'Live' | 'Demo';
    const isCMCPlatform = platform === 'CMC';
    const isMetaTrader = platform === 'METATRADER' || product.startsWith('MT');
    const isCorporate = customerType === 'CORPORATE';
    const isSpectre = product.includes('SPECTRE');
    const isLiveContext = context === 'Live';

    const forceNoModal = appContext.ForceNoModal === true || appContext.ForceNoModal === 'true';

    const showCfdAddOnModal =
      !forceNoModal &&
      !isAddOnJourney &&
      !addOnModalDismissed &&
      runtimeProduct === 'EQUITY' &&
      !NON_EQUITY_JOURNEY_PRODUCTS.has(journeyProduct) &&
      isCMCPlatform &&
      isLiveContext &&
      effectiveRegion !== 'SG'; /** Remove after day one */

    let thankYouScreen;
    const isCFDProduct = product === 'CFD';
    const sumsubUrl = appContext?.SumsubVerificationLink || '';
    if (sumsubUrl || (isLiveContext && localeRegion === 'AT')) {
      thankYouScreen = <ThankYouScreenSumsub region={localeRegion} sumsubUrl={sumsubUrl} embedded={embedded} />;
    } else if (isAddOnJourney && (isCancelled || !isCFDProduct)) {
      thankYouScreen = (
        <ThankYouScreenMultiAsset
          embedded={embedded}
          csrfToken={csrfToken}
          context={context}
          userEmail={userEmail}
          userPassword={userPassword}
        />
      );
    } else if (isAddOnJourney) {
      thankYouScreen = (
        <ThankYouScreenAddOn
          embedded={embedded}
          csrfToken={csrfToken}
          userEmail={userEmail}
          userPassword={userPassword}
        />
      );
    } else if (isCorporate) {
      thankYouScreen = (
        <ThankYouScreenCorporate
          embedded={embedded}
          csrfToken={csrfToken}
          userEmail={userEmail}
          userPassword={userPassword}
        />
      );
    } else if (isMetaTrader) {
      thankYouScreen = (
        <ThankYouScreenMetaTrader
          embedded={embedded}
          csrfToken={csrfToken}
          context={context}
          userEmail={userEmail}
          userPassword={userPassword}
        />
      );
    } else if (isSpectre) {
      thankYouScreen = (
        <ThankYouScreenSpectre
          embedded={embedded}
          csrfToken={csrfToken}
          userEmail={userEmail}
          userPassword={userPassword}
        />
      );
    } else {
      thankYouScreen = (
        <ThankYouScreenMultiAsset
          embedded={embedded}
          csrfToken={csrfToken}
          context={context}
          userEmail={userEmail}
          userPassword={userPassword}
        />
      );
    }

    return (
      <>
        {thankYouScreen}
        {showCfdAddOnModal && <CfdAddOnModal onConfirm={handleAddOnConfirm} onDismiss={handleAddOnDismiss} />}
      </>
    );
  }
  const showApplicationsModal =
    !journeyStartedRef.current && pendingApplications !== undefined && pendingApplications.length > 0;

  return (
    <>
      {showApplicationsModal && (
        <InProgressApplicationsModal
          applications={pendingApplications}
          locale={effectiveLocale}
          canStartNew={canAddAccount}
          onSelectApplication={handleSelectApplication}
          onStartNew={handleStartNewApplication}
        />
      )}
      {pegaState && (
        <PegaEmbed
          infinityServer={pegaBaseUrl}
          token={pegaToken!}
          caseTypeID={!pegaState?.applicationAssignmentKey ? caseTypeId : undefined}
          assignmentID={pegaState?.applicationAssignmentKey}
          appID={getPegaAppAlias()}
          loadingDone={(status) => {
            setLoadingStatus(status);
            if (status) {
              subscribeToPegaEvents();
            }
          }}
          startingFields={
            isAddOnJourney
              ? { ...addOnConfig.pegaStartingFields, Channel: channel }
              : { ...pegaStartingFields, Channel: channel }
          }
        />
      )}
      {loadingStatus === undefined && !showApplicationsModal && (
        <BaseScreen
          applicationSubmitted={applicationSubmitted}
          formTitle={''}
          footerBtnLabel={'Continue'}
          footerBtnOnClick={() => {}}
          footerBtnLoading={false}
          footerBtnDisabled={true}
        >
          {error ? (
            <ErrorMessage message={error} />
          ) : (
            <div className="loading-overlay active" id="loadingOverlay">
              <div id="loaderContainer">
                <div className="loader-4"></div>
              </div>
            </div>
          )}
        </BaseScreen>
      )}
    </>
  );
};
